<!--
  A styled radio group field with a label, validation, and two-way binding for the selected value.
  
  "options" prop is required, and should be an object with keys representing the human-readable option names to be displayed in the dropdown,
  and values representing the corresponding model values which we're actually binding to.
 -->
<template>
  <div class="fieldWrapper field">
    <div style="display:flex">
      <label v-if="label" class="label">{{ label }}</label>
      <slot name="top"/>
    </div>

    <p v-if="description" class="mb-2">{{ description }}</p>

    <div class="control radioGroup" :class="{stacked: stacked}">
      <label class="radioLabel"
        v-for="(optionValue, optionLabel, idx) in allowedOptions"
        :key="optionValue"
        :disabled="isDisabled"
      >
        <input type="radio" :name="radioGroupId" :checked="optionValue == modelValue" @click="onChanged(optionValue)">
        {{optionLabel}} <slot v-if="optionValue == modelValue && idx==0" name="first-option"/>
      </label>

      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { generateRandomId } from "@/core/utils";
import { computed, ref, onMounted } from "vue";

const props = defineProps<{
  options: object,
  includedOptions?: any[],
  excludedOptions?: any[],
  modelValue: any|null,
  label?: string,
  description?: string,
  stacked?: boolean,
  validator?: (fieldId: any, val: any) => boolean,
  validationId?: string,
  isDisabled?: boolean
}>();

// Need a unique ID for the radio button group
let radioGroupId = ref(generateRandomId());

// Event for two-way binding
const emit = defineEmits(['update:modelValue']);
const onChanged = (val: any) => {
  emit('update:modelValue', val);
};

const allowedOptions = computed(() => {
  if (props.includedOptions) {
    return Object.fromEntries(
      Object.entries(props.options).filter(([k,v]) => props.includedOptions!.includes(v))
    );
  } else if (props.excludedOptions) {
    return Object.fromEntries(
      Object.entries(props.options).filter(([k,v]) => !props.excludedOptions!.includes(v))
    );
  } else {
    return props.options;
  }  
});

</script>

<style lang="scss" scoped>
@import "../assets/variables.scss";

.radioGroup {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.75rem;
}

@media screen and (max-width: $tabletWidth) {
  .radioGroup {
    align-items: start;
    flex-direction: column;
    gap: 0rem;
  }
}

.stacked {
  align-items: start;
  flex-direction: column;
  gap: 0rem;
}

.radioLabel {
  padding: 0.5rem;
}

input[type=radio] {
  scale: 150%;
  position: relative;
  left: -0.25rem;
  top: -0.1rem;
  margin-right: 0.1rem;
}
</style>
 